import type { ReactElement } from 'react';
import type { DeliveryAddressDirectoryEntry, ProcurementRequest, StatusFilter, UserRole } from '../../domain/types';
import {
  deliveryAddressLabelForId,
  materialsSummary,
  requestDisplayTitle,
  totalMaterialQuantity
} from '../../domain/types';
import { formatStatus } from '../../domain/formatters';
import { wellRegionLabel } from '../../domain/wellRegions';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

const REQUEST_COLUMNS: GroupedColumnDef[] = [
  { id: 'requestId', label: 'Request id', header: 'Request id' },
  { id: 'wellTitle', label: 'Well / title', header: 'Well / title' },
  { id: 'requestor', label: 'Requestor', header: 'Requestor' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'afeNumber', label: 'AFE #', header: 'AFE #' },
  { id: 'field', label: 'Field', header: 'Field' },
  { id: 'delivery', label: 'Delivery', header: 'Delivery' },
  { id: 'materials', label: 'Materials', header: 'Materials' },
  { id: 'qty', label: 'Qty', header: 'Qty' },
  { id: 'status', label: 'Status', header: 'Status' },
  { id: 'requiredBy', label: 'Required by', header: 'Required by' },
  { id: 'created', label: 'Created', header: 'Created' }
];

interface RequestsGridProps {
  role: UserRole;
  rows: ProcurementRequest[];
  selectedId: string | null;
  onSelectRequest: (id: string) => void;
  onOpenRequestInPage?: (id: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (filter: StatusFilter) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onNewRequest?: () => void;
  /** Resolve delivery column labels (optional; ids shown if empty). */
  deliveryAddresses?: DeliveryAddressDirectoryEntry[];
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc' | null;
  onSortClick: (columnId: string) => void;
}

export function RequestsGrid({
  role,
  rows,
  selectedId,
  onSelectRequest,
  onOpenRequestInPage,
  statusFilter,
  onStatusFilterChange,
  page: currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onNewRequest,
  deliveryAddresses = [],
  sortColumn,
  sortDirection,
  onSortClick
}: RequestsGridProps): ReactElement {
  const serverBackedSort =
    (sortColumn === 'created' || sortColumn === 'requiredBy') && sortDirection !== null;
  function getGroupValue(request: ProcurementRequest, columnId: string): string {
    switch (columnId) {
      case 'requestId':
        return request.requestId ?? '—';
      case 'wellTitle':
        return requestDisplayTitle(request);
      case 'requestor':
        return request.requestor?.name ?? '—';
      case 'region':
        return request.requestor?.region ? wellRegionLabel(request.requestor.region) : '—';
      case 'afeNumber':
        return request.afeNumber?.trim() || '—';
      case 'field':
        return (
          request.regionFieldInfo?.name?.trim() ||
          request.wellInfo?.field?.trim() ||
          '—'
        );
      case 'delivery':
        return deliveryAddressLabelForId(request.deliveryAddressId, deliveryAddresses);
      case 'materials':
        return materialsSummary(request);
      case 'qty': {
        const q = totalMaterialQuantity(request);
        return q ? String(q) : '—';
      }
      case 'status':
        return formatStatus(request.status);
      case 'requiredBy':
        return request.timeline?.requiredByDate?.trim() || '—';
      case 'created':
        return new Date(request.createdAtUtc).toLocaleString();
      default:
        return '—';
    }
  }

  function renderRequestRow(request: ProcurementRequest): ReactElement {
    return (
      <tr
        className={selectedId === request.id ? 'selected' : undefined}
        onClick={() => onSelectRequest(request.id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          onOpenRequestInPage?.(request.id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectRequest(request.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td>{request.requestId ?? '—'}</td>
        <td>{requestDisplayTitle(request)}</td>
        <td>{request.requestor?.name ?? '—'}</td>
        <td>{request.requestor?.region ? wellRegionLabel(request.requestor.region) : '—'}</td>
        <td>{request.afeNumber?.trim() || '—'}</td>
        <td>{request.regionFieldInfo?.name?.trim() || request.wellInfo?.field?.trim() || '—'}</td>
        <td>{deliveryAddressLabelForId(request.deliveryAddressId, deliveryAddresses)}</td>
        <td>{materialsSummary(request)}</td>
        <td>{totalMaterialQuantity(request) || '—'}</td>
        <td>
          <span className={`status-pill status-${request.status}`}>{formatStatus(request.status)}</span>
        </td>
        <td>{request.timeline?.requiredByDate?.trim() || '—'}</td>
        <td>{new Date(request.createdAtUtc).toLocaleString()}</td>
      </tr>
    );
  }

  return (
    <section className="card requests-grid-card">
      <div className="row spread requests-grid-toolbar">
        <h2>Requests</h2>
        {onNewRequest && (
          <button
            type="button"
            className="secondary well-add-btn"
            title="New procurement request"
            aria-label="New procurement request"
            onClick={(e) => {
              e.stopPropagation();
              onNewRequest();
            }}
          >
            +
          </button>
        )}
      </div>
      <p className="hint">
        Click a row for the side panel; double-click to open the full page
        {role === 'RegionalManager' ? ' (approve or reject from either view)' : ''}.
      </p>
      <div className="row filter-row">
        <label htmlFor="statusFilter">Status</label>
        <select
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value as StatusFilter)}
        >
          <option value="all">All statuses</option>
          {role !== 'RegionalManager' && <option value="draft">Draft</option>}
          <option value="pendingApproval">Pending approval</option>
          <option value="procurementReview">Procurement review</option>
          <option value="sourcing">Sourcing</option>
          <option value="orderPlaced">Order placed</option>
          <option value="procured">Procured</option>
          <option value="complete">Complete</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <GroupedDataGrid<ProcurementRequest>
        columns={REQUEST_COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={getGroupValue}
        renderRow={renderRequestRow}
        colSpan={12}
        emptyMessage="No requests match this status."
        groupHint="Created and Required by sort the full filtered list (all pages) by date/time. Other columns sort this page only. Drag the grip (⠿) beside a title into the strip above to group."
        controlledSortColumn={sortColumn}
        controlledSortDirection={sortDirection}
        onControlledSortClick={onSortClick}
        trustServerRowOrder={serverBackedSort}
      />
      <GridPaginationBar
        idPrefix="requests-grid"
        page={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
